import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import { FaArrowLeft, FaChevronRight, FaFilePdf, FaEye } from 'react-icons/fa';
import { db } from '../../firebase';
import { ref, onValue, update, get } from 'firebase/database';
import { toast } from 'react-toastify';
import emailjs from '@emailjs/browser';

const NewSubheStatus = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userData = location.state?.userData || null;

    const [status, setStatus] = useState('');
    const [remarks, setRemarks] = useState('');
    const [uploadedDocs, setUploadedDocs] = useState({});
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    const documentList = [
        { id: 1, name: 'शासनमान्य संस्थेचे प्रमाणपत्र' },
        { id: 2, name: 'शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक' },
        { id: 3, name: 'अधिवास प्रमाणपत्र' },
        { id: 4, name: 'पास पोर्ट साईझ फोटो' },
        { id: 5, name: 'नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो काढून फोटो साक्षांकित करावा' },
        { id: 6, name: 'आधार कार्ड' },
        { id: 7, name: 'पॅन कार्ड' },
        { id: 8, name: 'अर्जदाराची स्वाक्षरी' },
        { id: 9, name: 'प्रतिज्ञा पत्र' },
        { id: 10, name: 'रोजगार कार्ड' },
        { id: 11, name: 'DSC टोकन' },
        { id: 12, name: 'Other Documents' },
    ];

    useEffect(() => {
        if (!userData) {
            navigate('/main-view-subhe-form');
            return;
        }

        setStatus(userData.assistantStatus || '');
        setRemarks(userData.assistantRemark || '');

        const docsRef = ref(db, `ekamwatapzp/EngineerRegistrations/${userData.id}/documents`);
        const unsubscribe = onValue(docsRef, (snapshot) => {
            if (snapshot.exists()) {
                setUploadedDocs(snapshot.val());
            } else {
                setUploadedDocs({});
            }
            setLoading(false);
        });

    return () => unsubscribe();
    }, [userData, navigate]);

    const generateSequentialPassword = async () => {
        let maxNum = 1000;
        try {
            const nodes = [
                'EngineerRegistrations',
                'SocietyRegistrations',
                'EngineerLotteryRegistrations',
                'SocietyLotteryRegistrations'
            ];

            for (const node of nodes) {
                const snapshot = await get(ref(db, `ekamwatapzp/${node}`));
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    Object.values(data).forEach(item => {
                        if (item.password?.startsWith("zp-")) {
                            const num = parseInt(item.password.split("-")[1]);
                            if (!isNaN(num) && num > maxNum) {
                                maxNum = num;
                            }
                        }
                    });
                }
            }
        } catch (error) {
            console.error("Error generating sequential password:", error);
        }
        return `zp-${maxNum + 1}`;
    };

    const sendEmail = async (email, name, password) => {
        if (!email) return;
        try {
            const templateParams = {
                to_email: email,
                to_name: name,
                password: password
            };
            await emailjs.send(
                "service_l4dlr5d",
                "template_7fp2221",
                templateParams,
                "v-QZUEXspyjyNUJ-F"
            );
        } catch (error) {
            console.error("Email error:", error);
        }
    };

    const handleUpdateStatus = async () => {
        if (!status) {
            toast.error("कृपया स्थिती निवडा");
            return;
        }

        setUpdating(true);
        try {
            const userRef = ref(db, `ekamwatapzp/EngineerRegistrations/${userData.id}`);
            const updatePayload = {
                assistantStatus: status,
                assistantRemark: remarks,
                statusUpdatedAt: new Date().toISOString()
            };

            // If approved, generate password and send email
            if (status === 'Approved') {
                const newPassword = await generateSequentialPassword();
                updatePayload.password = newPassword;
                
                // Send email notification
                await sendEmail(
                    userData.email,
                    `${userData.firstName} ${userData.surname}`,
                    newPassword
                );
                
                toast.success(`Approving and sending password: ${newPassword}`);
            }

            await update(userRef, updatePayload);
            toast.success("स्थिती यशस्वीरित्या अपडेट झाली!");
            setTimeout(() => navigate(-1), 1500);
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("स्थिती अपडेट करताना त्रुटी आली");
        } finally {
            setUpdating(false);
        }
    };

    if (!userData) return null;

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 p-4 md:p-6 text-gray-900">
                {/* Header & Breadcrumb */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm"
                        >
                            <FaArrowLeft size={14} />
                        </button>
                        <div>
                            <p className="text-xs font-medium text-gray-500">अर्जदाराची कागदपत्रे</p>
                            <h1 className="text-xl font-semibold text-gray-800">अर्जदार: {userData.surname} {userData.firstName} {userData.middleName}</h1>
                        </div>
                    </div>
                    <div className="flex items-center text-xs font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <FaChevronRight size={8} className="text-gray-400" />
                        <span className="text-blue-600">अर्जदाराची कागदपत्रे</span>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 md:p-8 space-y-8">

                        {/* Documents Table */}
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                        <th className="px-4 py-3 w-16 text-center">#</th>
                                        <th className="px-4 py-3">कागदपत्राचे नाव (Document Name)</th>
                                        <th className="px-4 py-3 text-center">पहा (View)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {documentList.map((doc) => {
                                        const uploaded = uploadedDocs[doc.id];
                                        return (
                                            <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 text-sm text-gray-500 text-center">{doc.id}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700 font-medium">{doc.name}</td>
                                                <td className="px-4 py-3 text-center">
                                                    {uploaded ? (
                                                        <a
                                                            href={uploaded.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium hover:bg-blue-600 hover:text-white transition-all border border-blue-200"
                                                        >
                                                            <FaEye size={12} /> View Document
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">उपलब्ध नाही</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Challan Section Header */}
                        <div className="border-t border-gray-100 pt-8">
                            <h3 className="text-sm font-semibold text-gray-800 mb-4">नोंदणी आणि फी माहिती</h3>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                            <th className="px-4 py-3 w-16 text-center">#</th>
                                            <th className="px-4 py-3">कागदपत्राचे नाव</th>
                                            <th className="px-4 py-3 text-center">पहा</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 text-sm text-gray-500 text-center">1</td>
                                            <td className="px-4 py-3 text-sm text-gray-700 font-medium">नोंदणी चलन आणि फी चलन</td>
                                            <td className="px-4 py-3 text-center">
                                                {userData.payments?.["Registration invoice"]?.receiptUrl ? (
                                                    <a
                                                        href={userData.payments["Registration invoice"].receiptUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium hover:bg-emerald-600 hover:text-white transition-all border border-emerald-200"
                                                    >
                                                        <FaFilePdf size={12} /> View Challan
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-gray-400">उपलब्ध नाही</span>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Incharge Status Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8 border-t border-gray-100">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p className="text-xs font-medium text-gray-500 mb-1">प्रलंबित स्थिती (Current Status):</p>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${userData.assistantStatus === 'Approved' ? 'text-green-700 bg-green-50 border-green-200' :
                                    userData.assistantStatus === 'Rejected' ? 'text-red-700 bg-red-50 border-red-200' :
                                        'text-amber-700 bg-amber-50 border-amber-200'
                                    }`}>
                                    {userData.assistantStatus || 'Pending'}
                                </span>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p className="text-xs font-medium text-gray-500 mb-1">अभिप्राय (Remark):</p>
                                <p className="text-sm font-medium text-gray-700">{userData.assistantRemark || 'No Remark'}</p>
                            </div>
                        </div>

                        {/* Status Update Form */}
                        <div className="space-y-6 pt-4">
                            <div className="flex flex-col space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">निकाल निवडा (Status) <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all appearance-none"
                                    >
                                        <option value="">स्थिती निवडा (Select Status)</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Clarification Required">Clarification Required</option>
                                    </select>
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">अभिप्राय लिहा (Remarks)</label>
                                <textarea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    rows={3}
                                    className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all resize-none"
                                    placeholder="तुमचा अभिप्राय येथे लिहा..."
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3 pt-4">
                                <button
                                    onClick={handleUpdateStatus}
                                    disabled={updating}
                                    className="bg-blue-600 text-white px-6 py-2 rounded text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
                                >
                                    {updating ? 'अपडेट होत आहे...' : 'अपडेट करा (Update Status)'}
                                </button>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="bg-gray-100 text-gray-700 border border-gray-300 px-6 py-2 rounded text-sm font-medium hover:bg-gray-200 transition"
                                >
                                    रद्द करा (Cancel)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Copyright */}
                <div className="mt-8 text-center pb-8">
                    <p className="text-[11px] text-gray-400 font-medium">
                        © 2026 सांगली जिल्हा परिषद. सर्व हक्क राखीव.
                    </p>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default NewSubheStatus;
